import { CSSProperties, memo, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { Icon } from '~/components/atomic';
import { useGetDartsQuery } from '~/features/dart';
import { GAEvent } from '~/utils';
import { GA } from '~/constants';
import dart from '~/assets/img/icons/dart.svg';
import { useUserStore } from '~/store';
import useDartBottomSheetStore from '~/store/useDartBottomSheetStore';
import DartBottomSheet from '../BottomSheet/Dart/DartBottomSheet';
import GlobalFloatLayout from '../GlobalLayout/GlobalFloatLayout';
import {
  Container,
  DartBalance,
  DartButtonContainer,
  DartIcon,
  HeaderSectionWrapper,
  TitleContainer,
  TitleText,
} from './Header.styled';

interface HeaderCommonProps {
  children: ReactNode;
  style?: CSSProperties;
}

interface HeaderProps extends HeaderCommonProps {
  transparentBg?: boolean;
}
interface HeaderContainerProps extends HeaderCommonProps {
  gap?: number;
}
interface HeaderTitleProps extends HeaderCommonProps {
  boldText?: boolean;
}

interface PrevButtonProps {
  onPrevClick?: () => void;
}
interface DartProps {
  transparentBg?: boolean;
}

const Header = ({
  children,
  style = {},
  transparentBg = false,
}: HeaderProps) => {
  return (
    <GlobalFloatLayout type='header' transparentBg={transparentBg}>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          > div {
            display: flex;
            align-items: center;
          }
        `}
        style={style}
      >
        {children}
      </div>
    </GlobalFloatLayout>
  );
};

const HeaderSection = ({
  children,
  gap = 0,
  style = {},
}: HeaderContainerProps) => {
  return (
    <HeaderSectionWrapper gap={gap} style={style}>
      {children}
    </HeaderSectionWrapper>
  );
};

const PrevButton = ({ onPrevClick }: PrevButtonProps) => {
  const navigate = useNavigate();

  const handlePrevPage = () => {
    navigate(-1);
  };

  return (
    <Container onClick={onPrevClick ? onPrevClick : handlePrevPage}>
      <Icon size={28} iconName={'ArrowLeft'} />
    </Container>
  );
};

const Title = ({ style, children, boldText = false }: HeaderTitleProps) => {
  return (
    <TitleContainer>
      <TitleText boldText={boldText} style={style}>
        {children}
      </TitleText>
    </TitleContainer>
  );
};

const Dart = memo(({ transparentBg = true }: DartProps) => {
  const email = useUserStore((state) => state.email);
  const { setIsOpen } = useDartBottomSheetStore();

  const { data: dartBalance } = useGetDartsQuery();
  const handleClick = () => {
    // send GA event : CLICK_DARTSTORE
    GAEvent(email, GA.ACTION.CLICK_DARTSTORE, {});
    setIsOpen(true);
  };

  useEffect(() => {
    return () => {
      setIsOpen(false);
    };
  }, [setIsOpen]);

  return (
    <>
      <DartButtonContainer onClick={handleClick} transparentBg={transparentBg}>
        <DartIcon src={dart} alt='' />
        <DartBalance>{dartBalance}</DartBalance>
      </DartButtonContainer>
      <DartBottomSheet />
    </>
  );
});

Header.Section = HeaderSection;
Header.Title = Title;
Header.PrevButton = PrevButton;
Header.Dart = Dart;
Header.Root = Header;

export default Header;
